'use client'

import * as React from 'react'
import { Search, TrendingUp, Target, BarChart3, ArrowUpRight, ArrowDownRight } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Badge } from 'components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { cn } from 'lib/utils'
import { StatCard } from 'components/dashboard/stat-card'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../../../firebase'

const taskStatusStyles = {
  completed: { label: 'Completed', className: 'bg-success/15 text-success' },
  in_progress: { label: 'In Progress', className: 'bg-primary/15 text-primary' },
  delayed: { label: 'Delayed', className: 'bg-warning/15 text-warning' },
  not_started: { label: 'Not Started', className: 'bg-muted text-muted-foreground' },
}

function formatDate(value) {
  if (!value) return '—'
  const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-KE', { month: 'short', day: 'numeric', year: 'numeric' })
}

const statusProgress = {
  completed: 100,
  in_progress: 60,
  delayed: 40,
  not_started: 0,
}

export default function MELPage() {
  const [search, setSearch] = React.useState('')
  const [indicatorStats, setIndicatorStats] = React.useState({
    total: 0,
    onTrack: 0,
    atRisk: 0,
    offTrack: 0,
  })
  const [deliverableCards, setDeliverableCards] = React.useState([])
  const [performanceChartData, setPerformanceChartData] = React.useState([])
  const [taskBoards, setTaskBoards] = React.useState({
    not_started: [],
    in_progress: [],
    completed: [],
    delayed: [],
  })
  const [taskBoardsLoading, setTaskBoardsLoading] = React.useState(true)
  const [logframesByProject, setLogframesByProject] = React.useState([])
  const [logframesLoading, setLogframesLoading] = React.useState(true)

  const filtered = deliverableCards.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  )

  React.useEffect(() => {
    let isMounted = true

    async function loadTaskBoards() {
      try {
        setLogframesLoading(true)
        const [deliverableSnap, projectSnap, logframeSnap] = await Promise.all([
          getDocs(collection(db, 'deliverables')),
          getDocs(collection(db, 'projects')),
          getDocs(collection(db, 'projectLogframes')),
        ])
        if (!isMounted) return
        const deliverables = deliverableSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projects = projectSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const logframes = logframeSnap.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }))
        const projectMap = projects.reduce((acc, project) => {
          acc[project.id] = project
          return acc
        }, {})

        const grouped = {
          not_started: [],
          in_progress: [],
          completed: [],
          delayed: [],
        }
        const cards = []
        const chartRows = []
        const validStatuses = new Set(['completed', 'in_progress', 'in-progress', 'delayed', 'not_started', 'not-started'])
        const now = new Date()

        deliverables.forEach((item) => {
          const status = String(item.status || '').toLowerCase()
          if (!validStatuses.has(status)) return
          const normalized = status.replace(/-/g, '_')
          if (!grouped[normalized]) return
          const project = projectMap[item.projectId]
          cards.push({
            id: item.id,
            name: item.name || item.title || 'Task',
            status: normalized,
            projectCode: project?.code || project?.shortCode || project?.title || item.projectId || 'Project',
            owner: item.leadPersonName || item.responsiblePersonName || 'Unassigned',
            plannedStart: item.plannedStart,
            plannedEnd: item.plannedEnd,
          })

          const start = item.plannedStart?.toDate ? item.plannedStart.toDate() : new Date(item.plannedStart)
          const end = item.plannedEnd?.toDate ? item.plannedEnd.toDate() : new Date(item.plannedEnd)
          const hasDates = !Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end.getTime() > start.getTime()
          let planned = 0
          if (hasDates) {
            if (now <= start) planned = 0
            else if (now >= end) planned = 100
            else planned = Math.round(((now.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100)
          }
          const rawProgress = Number(item.progress)
          const actual = Number.isFinite(rawProgress)
            ? Math.max(0, Math.min(100, rawProgress))
            : statusProgress[normalized] ?? 0
          chartRows.push({
            name: (item.name || item.title || 'Task').slice(0, 25),
            planned,
            actual,
          })
          grouped[normalized].push({
            id: item.id,
            name: item.name || item.title || 'Task',
            projectCode: project?.code || project?.shortCode || project?.title || item.projectId || 'Project',
            owner: item.leadPersonName || item.responsiblePersonName || 'Unassigned',
            dueDate: item.plannedEnd,
          })
        })

        setTaskBoards(grouped)
        setDeliverableCards(cards)
        setPerformanceChartData(chartRows.slice(0, 12))
        const onTrack = grouped.completed.length + grouped.in_progress.length
        const atRisk = grouped.delayed.length
        const offTrack = grouped.not_started.length
        setIndicatorStats({
          total: onTrack + atRisk + offTrack,
          onTrack,
          atRisk,
          offTrack,
        })
        const groupedLogframes = logframes.reduce((acc, entry) => {
          const projectId = entry.projectId
          if (!projectId) return acc
          const project = projectMap[projectId]
          const key = projectId
          if (!acc[key]) {
            acc[key] = {
              projectId,
              projectTitle: project?.title || project?.code || 'Project',
              projectCode: project?.code || project?.shortCode || projectId,
              entries: [],
            }
          }
          acc[key].entries.push(entry)
          return acc
        }, {})
        const logframeCards = Object.values(groupedLogframes)
        setLogframesByProject(logframeCards)
      } catch {
        if (!isMounted) return
        setTaskBoards({
          not_started: [],
          in_progress: [],
          completed: [],
          delayed: [],
        })
        setDeliverableCards([])
        setPerformanceChartData([])
        setIndicatorStats({ total: 0, onTrack: 0, atRisk: 0, offTrack: 0 })
        setLogframesByProject([])
      } finally {
        if (isMounted) {
          setTaskBoardsLoading(false)
          setLogframesLoading(false)
        }
      }
    }

    loadTaskBoards()

    return () => {
      isMounted = false
    }
  }, [])

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
            MEL & Impact Assessment
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Results frameworks, indicators, data collection, and verification
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Indicators"
          value={taskBoardsLoading ? '—' : indicatorStats.total}
          icon={<Target className="size-5" />}
        />
        <StatCard
          title="On Track"
          value={taskBoardsLoading ? '—' : indicatorStats.onTrack}
          icon={<ArrowUpRight className="size-5" />}
          variant="success"
        />
        <StatCard
          title="At Risk"
          value={taskBoardsLoading ? '—' : indicatorStats.atRisk}
          icon={<TrendingUp className="size-5" />}
          variant="warning"
        />
        <StatCard
          title="Off Track"
          value={taskBoardsLoading ? '—' : indicatorStats.offTrack}
          icon={<ArrowDownRight className="size-5" />}
          variant="danger"
        />
      </div>

      <Tabs defaultValue="indicators">
        <TabsList>
          <TabsTrigger value="indicators">Indicators</TabsTrigger>
          <TabsTrigger value="chart">Performance Chart</TabsTrigger>
          <TabsTrigger value="logframes">Logframes</TabsTrigger>
        </TabsList>

        <TabsContent value="indicators" className="mt-4 flex flex-col gap-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search tasks..."
              className="pl-10 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {taskBoardsLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Loading tasks...</div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">No tasks found.</div>
            ) : (
              filtered.map((task) => {
                const statusStyle = taskStatusStyles[task.status] || taskStatusStyles.not_started

                return (
                  <Card key={task.id} className="border border-border">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex flex-col gap-1">
                            <h3 className="text-sm font-medium text-foreground leading-tight">
                              {task.name}
                            </h3>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] text-muted-foreground">
                                {task.projectCode} &middot; {task.owner}
                              </span>
                            </div>
                          </div>
                          <Badge className={cn('text-[10px] shrink-0', statusStyle.className)}>
                            {statusStyle.label}
                          </Badge>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex flex-col gap-0.5 rounded-lg bg-muted/50 p-2">
                            <span className="text-[10px] text-muted-foreground">Start</span>
                            <span className="text-xs font-semibold text-foreground">
                              {formatDate(task.plannedStart)}
                            </span>
                          </div>
                          <div className="flex flex-col gap-0.5 rounded-lg bg-primary/5 p-2">
                            <span className="text-[10px] text-muted-foreground">End</span>
                            <span className="text-xs font-semibold text-foreground">
                              {formatDate(task.plannedEnd)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                          <span>Status: {statusStyle.label}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })
            )}
          </div>
        </TabsContent>

        <TabsContent value="chart" className="mt-4">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="text-sm font-semibold text-foreground">
                Task Performance: Planned vs Actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={performanceChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.9 0.01 145)" />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="oklch(0.48 0.03 145)" />
                  <YAxis
                    dataKey="name"
                    type="category"
                    tick={{ fontSize: 10 }}
                    width={180}
                    stroke="oklch(0.48 0.03 145)"
                  />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="planned" fill="oklch(0.35 0.12 145)" name="Planned %" radius={[0, 3, 3, 0]} />
                  <Bar dataKey="actual" fill="oklch(0.87 0.15 85)" name="Actual %" radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="logframes" className="mt-4">
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-foreground">Indicators Task Board</CardTitle>
              <CardDescription className="text-xs">
                Tasks pulled from all project workplans
              </CardDescription>
            </CardHeader>
            <CardContent>
              {taskBoardsLoading ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Loading tasks...</div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {[
                    { key: 'not_started', label: 'Not Started' },
                    { key: 'in_progress', label: 'In Progress' },
                    { key: 'completed', label: 'Completed' },
                    { key: 'delayed', label: 'Delayed' },
                  ].map((col) => {
                    const tasks = taskBoards[col.key] || []
                    return (
                      <Card key={col.key} className="border border-border">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-xs font-semibold text-foreground">{col.label}</CardTitle>
                            <Badge variant="secondary" className="text-[10px]">{tasks.length}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {tasks.length === 0 ? (
                            <div className="rounded-lg border border-dashed p-3 text-center text-[10px] text-muted-foreground">
                              No tasks
                            </div>
                          ) : (
                            tasks.slice(0, 6).map((task) => (
                              <div key={task.id} className="rounded-lg border bg-card p-3">
                                <p className="text-xs font-semibold text-foreground truncate">{task.name}</p>
                                <p className="text-[10px] text-muted-foreground mt-1 truncate">
                                  {task.projectCode}
                                </p>
                                <div className="mt-2 flex items-center justify-between text-[10px] text-muted-foreground">
                                  <span className="truncate">{task.owner}</span>
                                  <span>{formatDate(task.dueDate)}</span>
                                </div>
                              </div>
                            ))
                          )}
                          {tasks.length > 6 ? (
                            <p className="text-[10px] text-muted-foreground">+{tasks.length - 6} more</p>
                          ) : null}
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
          {logframesLoading ? (
            <div className="py-6 text-center text-sm text-muted-foreground">Loading logframes...</div>
          ) : logframesByProject.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">No logframes found.</div>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {logframesByProject.map((project) => (
                <Card key={project.projectId} className="border border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-semibold text-foreground">
                      {project.projectTitle}
                    </CardTitle>
                    <CardDescription className="text-xs">{project.projectCode}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] text-muted-foreground uppercase">
                            <th className="text-left pb-2">Level</th>
                            <th className="text-left pb-2">Statement</th>
                            <th className="text-left pb-2">Indicators</th>
                          </tr>
                        </thead>
                        <tbody>
                          {project.entries.slice(0, 4).map((entry) => (
                            <tr key={entry.id} className="border-t border-border/40">
                              <td className="py-2 pr-2 capitalize">{entry.level || '—'}</td>
                              <td className="py-2 pr-2 truncate max-w-[180px]">{entry.statement || '—'}</td>
                              <td className="py-2 truncate max-w-[160px]">{entry.indicators || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {project.entries.length > 4 ? (
                        <p className="text-[10px] text-muted-foreground mt-2">+{project.entries.length - 4} more</p>
                      ) : null}
                    </div>
                    <Button variant="outline" size="sm" className="mt-3 w-full text-xs">
                      Open Full Logframe
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
